// pages/Cocina.jsx - Versión con fetch nativo sin credenciales
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  Statistic,
  Divider,
  Alert,
  Spin,
  Select,
  DatePicker,
  Button,
  message,
  Badge,
  Empty,
  Progress,
  Flex,
  ConfigProvider,
  Segmented,
  Tooltip,
  Modal,
  Input
} from 'antd';
import {
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  HomeOutlined,
  ReloadOutlined,
  CalendarOutlined,
  SettingOutlined,
  UserOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  SoundOutlined,
  BellOutlined,
  EnvironmentOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { motion, AnimatePresence } from 'framer-motion';

dayjs.locale('es');

const { Title, Text } = Typography;
const { Option } = Select;

// Configuración del hotel (se obtiene de la URL o localStorage)
const getHotelGuid = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const urlGuid = urlParams.get('hotelGuid');
  if (urlGuid) return urlGuid;
  return localStorage.getItem('cocina_hotelGuid') || '';
};

// Función para hacer fetch sin credenciales
const fetchWithoutAuth = async (url, params) => {
  const queryString = new URLSearchParams(params).toString();
  const fullUrl = `/api${url}${queryString ? `?${queryString}` : ''}`;
  
  const response = await fetch(fullUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'omit', // ¡IMPORTANTE! No envía cookies ni credenciales
    mode: 'cors'
  });
  
  if (!response.ok) {
    const error = new Error(`HTTP error! status: ${response.status}`);
    error.status = response.status;
    throw error;
  }
  
  return response.json();
};

const Cocina = () => {
  // Estados principales
  const [loading, setLoading] = useState(false);
  const [pendingData, setPendingData] = useState([]);
  const [stats, setStats] = useState({
    totalPending: 0,
    totalPeople: 0,
    roomsWithPending: 0,
    servicio: 'Desayuno',
    fecha: ''
  });
  
  // Estados para filtros
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedService, setSelectedService] = useState('Desayuno');
  const [hotelGuid, setHotelGuid] = useState(getHotelGuid());
  const [hotelName, setHotelName] = useState('');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [tempGuid, setTempGuid] = useState(hotelGuid);
  
  // Estados de UI
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(10);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sortBy, setSortBy] = useState('room');
  const [sortOrder, setSortOrder] = useState('asc');
  const [connectionError, setConnectionError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Refs
  const audioRef = useRef(null);
  const containerRef = useRef(null);
  const previousCountRef = useRef(0);
  
  // Servicios disponibles
  const servicios = [
    { value: 'Desayuno', label: 'Desayuno', icon: '☕', time: '07:00 - 10:00', color: '#faad14' },
    { value: 'Almuerzo', label: 'Almuerzo', icon: '🍽️', time: '13:00 - 15:00', color: '#52c41a' },
    { value: 'Cena', label: 'Cena', icon: '🌙', time: '20:00 - 22:00', color: '#1890ff' }
  ];
  
  // Obtener datos pendientes - SIN ENVIAR CREDENCIALES
  const fetchPendingData = useCallback(async () => {
    if (!hotelGuid || hotelGuid.trim() === '') {
      setShowConfigModal(true);
      setConnectionError(true);
      setErrorMessage('Por favor configure el GUID del hotel');
      return;
    }
    
    setLoading(true);
    setConnectionError(false);
    setErrorMessage('');
    
    try {
      const formattedDate = selectedDate.format('DD/MM/YYYY');
      const params = {
        hotelGuid: hotelGuid.trim(),
        fecha: formattedDate,
        servicio: selectedService
      };
      
      console.log('📡 Fetching data with params:', params);
      
      const data = await fetchWithoutAuth('/cocina/pending', params);
      
      if (data.success) {
        const newData = data.data;
        const newStats = data.stats;
        
        // Detectar cambios en el conteo para sonido
        if (soundEnabled && previousCountRef.current > 0 && 
            newStats.totalPending !== previousCountRef.current) {
          playSound();
        }
        
        previousCountRef.current = newStats.totalPending;
        setPendingData(newData || []);
        setStats(newStats);
        setLastRefresh(dayjs());
        console.log(`✅ Datos actualizados: ${newStats.totalPending} pendientes`);
      } else {
        throw new Error(data.error || 'Error al obtener datos');
      }
    } catch (error) {
      console.error('❌ Error obteniendo datos:', error);
      
      let errorMsg = 'Error al obtener datos de la cocina';
      if (error.status === 401) {
        errorMsg = 'Error de autenticación. Verifique la configuración del hotel.';
      } else if (error.status === 404) {
        errorMsg = 'Hotel no encontrado. Verifique el GUID.';
      } else if (error.status === 500) {
        errorMsg = 'Error del servidor. Contacte al administrador.';
      } else if (error.message?.includes('NetworkError') || error.message?.includes('Failed to fetch')) {
        errorMsg = 'Error de red. Verifique la conexión al servidor.';
      } else {
        errorMsg = error.message || 'Error desconocido';
      }
      
      setErrorMessage(errorMsg);
      setConnectionError(true);
      message.error(errorMsg);
      
      setPendingData([]);
      setStats({
        totalPending: 0,
        totalPeople: 0,
        roomsWithPending: 0,
        servicio: selectedService,
        fecha: selectedDate.format('DD/MM/YYYY')
      });
    } finally {
      setLoading(false);
    }
  }, [hotelGuid, selectedDate, selectedService, soundEnabled]);
  
  // Reproducir sonido de alerta
  const playSound = () => {
    if (audioRef.current) {
      audioRef.current.play().catch(e => console.log('Error reproduciendo sonido:', e));
    }
  };
  
  // Configuración de auto-refresh
  useEffect(() => {
    if (autoRefresh && hotelGuid && hotelGuid.trim() !== '') {
      const interval = setInterval(() => {
        fetchPendingData();
      }, refreshInterval * 1000);
      
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, fetchPendingData, hotelGuid]);
  
  // Cargar datos iniciales
  useEffect(() => {
    if (hotelGuid && hotelGuid.trim() !== '') {
      fetchPendingData();
      loadHotelConfig();
    }
  }, [hotelGuid, selectedDate, selectedService]);
  
  // Cargar configuración del hotel
  const loadHotelConfig = async () => {
    if (!hotelGuid || hotelGuid.trim() === '') return;
    
    try {
      const data = await fetchWithoutAuth('/cocina/config', { hotelGuid: hotelGuid.trim() });
      if (data.success) {
        setHotelName(data.data.hotelName);
      }
    } catch (error) {
      console.error('Error cargando configuración:', error);
    }
  };
  
  // Guardar configuración
  const saveConfig = () => {
    const guid = tempGuid?.trim();
    if (guid && guid !== '') {
      localStorage.setItem('cocina_hotelGuid', guid);
      setHotelGuid(guid);
      setShowConfigModal(false);
      message.success('Configuración guardada');
      // Limpiar errores
      setConnectionError(false);
      setErrorMessage('');
      fetchPendingData();
      loadHotelConfig();
    } else {
      message.error('Por favor ingrese un GUID válido');
    }
  };
  
  // Ordenar datos
  const getSortedData = () => {
    const sorted = [...pendingData];
    sorted.sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'room':
          aVal = a.Habitacion;
          bVal = b.Habitacion;
          break;
        case 'name':
          aVal = a.Nombre || '';
          bVal = b.Nombre || '';
          break;
        case 'regimen':
          aVal = a.Regimen || '';
          bVal = b.Regimen || '';
          break;
        default:
          aVal = a.Habitacion;
          bVal = b.Habitacion;
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    return sorted;
  };
  
  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };
  
  // Escuchar cambios de fullscreen
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);
  
  // Renderizar tarjeta de pendiente
  const renderPendingCard = (item, index) => (
    <motion.div
      key={`${item.NumReserva}-${item.Linea}-${item.NumOcupante}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Card
        style={{
          borderRadius: 20,
          marginBottom: 12,
          background: 'linear-gradient(135deg, #fff5e6 0%, #ffffff 100%)',
          border: '1px solid #ffd591',
          cursor: 'pointer',
          transition: 'all 0.3s'
        }}
        hoverable
        bodyStyle={{ padding: '16px' }}
      >
        <Row gutter={[16, 8]} align="middle">
          <Col xs={4} sm={3} md={2}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: 50,
                height: 50,
                borderRadius: 25,
                background: '#faad14',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto'
              }}>
                <Text style={{ fontSize: 24, fontWeight: 'bold', color: '#fff' }}>
                  {item.Habitacion}
                </Text>
              </div>
            </div>
          </Col>
          
          <Col xs={12} sm={9} md={12}>
            <div>
              <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 4 }}>
                {item.Nombre}
              </Text>
              <Space size={8} wrap>
                <Tag color="purple" style={{ margin: 0 }}>{item.Regimen}</Tag>
                <Space size={4}>
                  <span>👤 {item.Ad}</span>
                  {item.Ni > 0 && <span>👶 {item.Ni}</span>}
                  {item.Bb > 0 && <span>🍼 {item.Bb}</span>}
                </Space>
                {item.TodoIncluido && <Tag color="gold">Todo Incluido</Tag>}
                {item.BebidaIncluida && <Tag color="cyan">Bebida Incluida</Tag>}
              </Space>
            </div>
          </Col>
          
          <Col xs={8} sm={12} md={10} style={{ textAlign: 'right' }}>
            <Space>
              <Badge 
                count="Pendiente" 
                style={{ backgroundColor: '#faad14' }}
              />
              <Tag color="orange" icon={<ClockCircleOutlined />}>
                Esperando
              </Tag>
            </Space>
          </Col>
        </Row>
      </Card>
    </motion.div>
  );
  
  // Renderizar estadísticas
  const renderStats = () => {
    const currentService = servicios.find(s => s.value === selectedService);
    const progressPercent = stats.totalPeople > 0 
      ? ((stats.totalPeople - stats.totalPending) / stats.totalPeople * 100) 
      : 100;
    
    return (
      <Card style={{ borderRadius: 20, marginBottom: 24, background: '#fafafa' }}>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Statistic
              title="Pendientes"
              value={stats.totalPending}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14', fontSize: 32 }}
              suffix="personas"
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="Total comensales"
              value={stats.totalPeople}
              prefix={<TeamOutlined />}
              valueStyle={{ fontSize: 32 }}
              suffix="personas"
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="Habitaciones"
              value={stats.roomsWithPending}
              prefix={<HomeOutlined />}
              valueStyle={{ fontSize: 32 }}
            />
          </Col>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                Progreso
              </div>
              <Progress
                type="circle"
                percent={progressPercent}
                width={60}
                strokeColor="#52c41a"
              />
            </div>
          </Col>
        </Row>
        
        <Divider style={{ margin: '16px 0' }} />
        
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <Space size={8}>
            <Tag color={currentService?.color} style={{ fontSize: 14, padding: '4px 12px' }}>
              {currentService?.icon} {currentService?.label} • {currentService?.time}
            </Tag>
            <Tag icon={<CalendarOutlined />}>
              {selectedDate.format('dddd, DD/MM/YYYY')}
            </Tag>
          </Space>
          
          <Space size={8}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Última actualización: {lastRefresh?.format('HH:mm:ss') || '--:--:--'}
            </Text>
            <Badge 
              status={autoRefresh ? "processing" : "default"} 
              text={`Auto-refresh ${autoRefresh ? refreshInterval + 's' : 'off'}`}
            />
          </Space>
        </Flex>
        
        {connectionError && (
          <Alert
            message="Error de conexión"
            description={errorMessage || "No se pudo conectar con el servidor. Verifique que el GUID del hotel sea correcto."}
            type="error"
            showIcon
            style={{ marginTop: 16 }}
            action={
              <Button size="small" onClick={fetchPendingData} type="primary">
                Reintentar
              </Button>
            }
          />
        )}
      </Card>
    );
  };
  
  // Renderizar lista de pendientes
  const renderPendingList = () => {
    if (loading && pendingData.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Cargando pendientes...</Text>
          </div>
        </div>
      );
    }
    
    if (pendingData.length === 0 && !loading) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div>
              <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
              <Title level={4}>¡Todo listo!</Title>
              <Text type="secondary">
                No hay comensales pendientes para este servicio
              </Text>
            </div>
          }
          style={{ marginTop: 60 }}
        >
          <Button type="primary" onClick={fetchPendingData}>
            Actualizar
          </Button>
        </Empty>
      );
    }
    
    const sortedData = getSortedData();
    
    return (
      <div>
        <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
          <Text strong style={{ fontSize: 16 }}>
            📋 Comensales pendientes ({stats.totalPending})
          </Text>
          <Space size={8}>
            <Segmented
              size="small"
              value={sortBy}
              onChange={setSortBy}
              options={[
                { value: 'room', label: 'Habitación' },
                { value: 'name', label: 'Nombre' },
                { value: 'regimen', label: 'Régimen' },
              ]}
            />
            <Button
              size="small"
              icon={sortOrder === 'asc' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            />
          </Space>
        </Flex>
        
        <AnimatePresence>
          {sortedData.map((item, idx) => renderPendingCard(item, idx))}
        </AnimatePresence>
      </div>
    );
  };
  
  // Renderizar controles de cocina
  const renderKitchenControls = () => (
    <Card style={{ borderRadius: 20, marginBottom: 24 }}>
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={8}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text strong>Fecha</Text>
            <DatePicker
              value={selectedDate}
              onChange={setSelectedDate}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
              size="large"
            />
          </Space>
        </Col>
        
        <Col xs={24} sm={8}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text strong>Servicio</Text>
            <Select
              value={selectedService}
              onChange={setSelectedService}
              style={{ width: '100%' }}
              size="large"
            >
              {servicios.map(s => (
                <Option key={s.value} value={s.value}>
                  {s.icon} {s.label} ({s.time})
                </Option>
              ))}
            </Select>
          </Space>
        </Col>
        
        <Col xs={24} sm={8}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text strong>Actualización</Text>
            <Space>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={fetchPendingData}
                loading={loading}
                size="large"
              >
                Actualizar
              </Button>
              <Button
                icon={autoRefresh ? <BellOutlined /> : <BellOutlined />}
                onClick={() => setAutoRefresh(!autoRefresh)}
                type={autoRefresh ? 'primary' : 'default'}
                danger={!autoRefresh}
                size="large"
              >
                {autoRefresh ? `Auto ${refreshInterval}s` : 'Auto off'}
              </Button>
            </Space>
          </Space>
        </Col>
      </Row>
      
      <Divider style={{ margin: '16px 0' }} />
      
      <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
        <Space>
          <Text type="secondary">GUID Hotel:</Text>
          <Tag>{hotelGuid || 'No configurado'}</Tag>
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={() => setShowConfigModal(true)}
          >
            Cambiar
          </Button>
        </Space>
        
        <Space>
          <Tooltip title="Pantalla completa">
            <Button
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={toggleFullscreen}
            />
          </Tooltip>
          
          <Tooltip title="Sonido al actualizar">
            <Button
              icon={<SoundOutlined />}
              type={soundEnabled ? 'primary' : 'default'}
              onClick={() => setSoundEnabled(!soundEnabled)}
            />
          </Tooltip>
          
          <Select
            value={refreshInterval}
            onChange={setRefreshInterval}
            size="small"
            style={{ width: 100 }}
          >
            <Option value={5}>5 seg</Option>
            <Option value={10}>10 seg</Option>
            <Option value={15}>15 seg</Option>
            <Option value={30}>30 seg</Option>
            <Option value={60}>1 min</Option>
          </Select>
        </Space>
      </Flex>
    </Card>
  );
  
  return (
    <ConfigProvider
      theme={{
        components: {
          Card: { borderRadiusLG: 20 },
          Button: { borderRadius: 20 },
          Tag: { borderRadiusSM: 12 },
        }
      }}
    >
      <audio ref={audioRef} src="/notification.mp3" preload="auto" />
      
      <div ref={containerRef} style={{ 
        padding: isFullscreen ? 24 : 24,
        minHeight: '100vh',
        background: '#f0f2f5'
      }}>
        <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
          <div>
            <Title level={isFullscreen ? 2 : 3} style={{ marginBottom: 0 }}>
              🍳 Cocina - {selectedService}
            </Title>
            {hotelName && (
              <Text type="secondary">
                <EnvironmentOutlined /> {hotelName}
              </Text>
            )}
          </div>
          
          {pendingData.length > 0 && (
            <Badge 
              count={`${stats.totalPending} pendientes`}
              style={{ backgroundColor: '#faad14', fontSize: 14, padding: '0 12px' }}
            />
          )}
        </Flex>
        
        {renderKitchenControls()}
        {renderStats()}
        {renderPendingList()}
      </div>
      
      {/* Modal de configuración */}
      <Modal
        title="Configuración de Cocina"
        open={showConfigModal}
        onOk={saveConfig}
        onCancel={() => setShowConfigModal(false)}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text strong>GUID del Hotel</Text>
          <Input
            placeholder="Ingrese el GUID del hotel (idunico)"
            value={tempGuid}
            onChange={(e) => setTempGuid(e.target.value)}
            size="large"
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            El GUID se encuentra en la tabla dap_hoteles, campo idunico
          </Text>
          <Text type="secondary" style={{ fontSize: 12, color: '#ff4d4f' }}>
            Ejemplo: 479b4310-27b5-11f1-9a0c-fa163e31a3af
          </Text>
        </Space>
      </Modal>
    </ConfigProvider>
  );
};

export default Cocina;